/**
 * Signature progress for a form whose signature boxes come from its descriptor.
 *
 * The 204 hard-codes three slots because its form has three. These forms have
 * one or two depending on the document, so the shape of the completion object
 * is kept identical to `getSignatureCompletion` and only the slot list varies —
 * that way `Ics204VersionStatusBar`, `Ics204VersionsDialog`, and the progress
 * ring can be reused through their existing `getCompletion` override.
 */

import {
  ICS204_PREPARED_BY_SIGN_ROLE,
  type SignatureCompletionStatus,
  type WalaSignature,
} from '../planningView/widgets/AssignmentList/ics204SignatureSlots';
import type { IcsFormSignatureSlot } from '../hooks/icsFormVersionHooks';

export type FormCompletion = {
  status: SignatureCompletionStatus;
  completedCount: number;
  requiredTotal: number;
  percent: number;
  slots: (IcsFormSignatureSlot & { complete: boolean })[];
};

/** A box is complete when any tip stamp carries that slot's role (any-order safe). */
const isSlotComplete = (
  slot: IcsFormSignatureSlot,
  signatures: WalaSignature[]
): boolean => signatures.some((signature) => signature.role === slot.role);

export function buildFormCompletion(
  slots: IcsFormSignatureSlot[],
  signatures: WalaSignature[] | null | undefined
): FormCompletion {
  const safeSignatures = signatures ?? [];
  const withState = slots.map((slot) => ({
    ...slot,
    complete: isSlotComplete(slot, safeSignatures),
  }));
  const requiredTotal = withState.length;
  const completedCount = withState.filter((slot) => slot.complete).length;

  let status: SignatureCompletionStatus = 'draft';
  if (safeSignatures.length === 0 || completedCount === 0) {
    status = 'draft';
  } else if (requiredTotal > 0 && completedCount >= requiredTotal) {
    status = 'signed';
  } else {
    status = 'partially-signed';
  }

  return {
    status,
    completedCount: Math.min(completedCount, requiredTotal),
    requiredTotal,
    percent:
      requiredTotal === 0
        ? 0
        : Math.round(
            (Math.min(completedCount, requiredTotal) / requiredTotal) * 100
          ),
    slots: withState,
  };
}

/** The next slot awaiting a signature, or undefined when the form is complete. */
export function nextOpenSlot(
  completion: FormCompletion
): (IcsFormSignatureSlot & { complete: boolean }) | undefined {
  return completion.slots.find((slot) => !slot.complete);
}

export { ICS204_PREPARED_BY_SIGN_ROLE };
